// ink-component
import React, { ChangeEvent, ReactNode, RefObject, useContext } from 'react';

import DropdownContext from '../../contexts/DropdownContext';
import NewCheckbox, { NewCheckboxProps } from '../../../NewCheckbox/NewCheckbox';
import useInkAnalytics from '../../../../hooks/useInkAnalytics';

export interface DropdownCheckboxProps extends NewCheckboxProps {
  /** Unique identifier */
  id: string;
  /** Checkbox label */
  label?: ReactNode;
  /** Callback handler (e.target.checked) */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLInputElement>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
}

/**
 * @purpose A checkbox for Dropdown.Item(s)
 * @categories navigation
 * @group Dropdown
 */
function DropdownCheckbox(props: DropdownCheckboxProps) {
  const { id, onChange, className, style, externalRef, ...otherProps } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef });

  const { close, preventClose } = useContext(DropdownContext);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    if (onChange) {
      onChange(event);
    }
    if (!preventClose) {
      close();
    }
  };

  return (
    <label className={className} tabIndex={-1} htmlFor={id}>
      <NewCheckbox
        id={id}
        onChange={handleChange}
        {...otherProps}
        externalRef={ref}
        data-track-name={undefined}
        role="checkbox"
        ignoreErrorContext
      />
    </label>
  );
}

DropdownCheckbox.defaultProps = {
  label: undefined,
  onChange: undefined,
  'data-track-events': ['change'],
};

DropdownCheckbox.displayName = 'Dropdown.Checkbox';

export default DropdownCheckbox;
